/**
 * Owns the entire stateful lifecycle of the branching CMM questionnaire:
 * the workflow document, the transformed question list, the pristine
 * answer snapshot, per-question patch/submit/cancel handlers, the visible
 * chain, and the overall validity flag.
 *
 * Consumers render the returned `visibleQuestions` and wire the returned
 * handlers to the questionnaire components — this hook contains no JSX.
 *
 * @module pharmaQuestionaire/hooks/usePharmaQuestionnaireState
 */
import { AnalysisFeedbackPayload } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { QuestionPatch } from "../../../components/questionnaire";
import {
  Answer,
  PharmaBranchedQuestionnaire,
  PharmaBranchedQuestionnaireDocument,
  QuestionForUI,
  UserFeedback,
} from "../../../data-model/cmmMedicalNeccessityQuestionnaire";
import {
  appendUserFeedbackToCmmWorkflow,
  applyPatchToCmmWorkflow,
  clearUserFeedbackFromCmmWorkflow,
  transformCmmWorkflowToQuestionnaireForUI,
} from "../../../utils/transformCmmQuestionnaire";
import { areAnswersEqual } from "../utils/answerEquality";
import { buildOriginalAnswerMap } from "../utils/originalAnswers";
import { validateQuestionnaireAnswers } from "../utils/validateQuestionnaireAnswers";
import { getVisibleQuestionChain } from "../utils/visibleQuestionChain";

/** Payload shape emitted by the in-question feedback submit button. */
type FeedbackPayload = AnalysisFeedbackPayload & { questionName: string };

/** Argument shape consumed by {@link applyPatchToCmmWorkflow}. */
type WorkflowPatchArg =
  | { name: string; answer: string | string[] }
  | { name: string; choiceText: string; freeText: string };

/**
 * Wraps a {@link PharmaBranchedQuestionnaire} payload into the {@link PharmaBranchedQuestionnaireDocument}
 * shape that the workflow helpers (`transformCmmWorkflowToQuestionnaireForUI`,
 * `applyPatchToCmmWorkflow`, etc.) operate on.
 *
 * The helpers all read from `doc.cmm_data_latest`, so wrapping is enough to
 * keep them happy without duplicating their logic.
 */
const wrapAsWorkflowDocument = (
  data: PharmaBranchedQuestionnaire,
): PharmaBranchedQuestionnaireDocument => ({
  cmm_data_latest: data,
});

/**
 * Pulls the {@link PharmaBranchedQuestionnaire} payload back out of a
 * {@link PharmaBranchedQuestionnaireDocument} returned by the workflow helpers, so the
 * hook can keep storing state in the API-native shape.
 */
const unwrapWorkflowDocument = (
  doc: PharmaBranchedQuestionnaireDocument,
): PharmaBranchedQuestionnaire => doc.cmm_data_latest;

/**
 * Normalizes a {@link QuestionPatch} from the questionnaire UI into the
 * argument shape expected by {@link applyPatchToCmmWorkflow}.
 *
 * - Answer patches → `{ name, answer }`.
 * - Free-text patches → `{ name, choiceText, freeText }`.
 *
 * @param patch - UI-side patch emitted by a question component
 * @returns Workflow-helper-shaped patch argument
 */
const toWorkflowPatchArg = (patch: QuestionPatch): WorkflowPatchArg =>
  "answer" in patch
    ? { name: patch.name, answer: patch.answer }
    : {
        name: patch.name,
        choiceText: patch.free_text_answer.name,
        freeText: patch.free_text_answer.answer,
      };

/**
 * Returns a new {@link Set} with `name` added when `shouldInclude` is
 * `true`, or removed otherwise. Returns the same `prev` reference (no
 * allocation) when no change is needed, so React can bail out of the
 * `setState` and avoid an unnecessary re-render.
 */
const toggleNameInSet = (
  prev: Set<string>,
  name: string,
  shouldInclude: boolean,
): Set<string> => {
  if (shouldInclude && prev.has(name)) return prev;
  if (!shouldInclude && !prev.has(name)) return prev;
  const next = new Set(prev);
  if (shouldInclude) {
    next.add(name);
  } else {
    next.delete(name);
  }
  return next;
};

/**
 * Manages the workflow document, derived questions, validation, and the
 * three user-edit handlers (patch / submit / cancel) for the branching
 * CMM questionnaire.
 *
 * @param currentDocument - Pristine workflow document; captured once and
 *                          used as the source of truth for "original" answers
 * @param userEmail       - Email of the current user, attached to any
 *                          appended {@link UserFeedback}
 * @returns Visible chain, validity, edited-question list, and edit handlers
 */
export const usePharmaQuestionnaireState = (
  currentDocument: PharmaBranchedQuestionnaire,
  initialDocument: PharmaBranchedQuestionnaire,
  userEmail: string,
) => {
  const [workflowDocument, setWorkflowDocument] =
    useState<PharmaBranchedQuestionnaire>(currentDocument);

  const originalDocumentRef =
    useRef<PharmaBranchedQuestionnaire>(initialDocument);

  useEffect(() => {
    setWorkflowDocument(currentDocument);
    setQuestions(
      transformCmmWorkflowToQuestionnaireForUI(
        wrapAsWorkflowDocument(currentDocument),
      ).questions,
    );
  }, [currentDocument]);

  useEffect(() => {
    originalDocumentRef.current = initialDocument;
    originalAnswersByNameRef.current = buildOriginalAnswerMap(initialDocument);
  }, [initialDocument]);

  /**
   * Snapshot of every question's original `answer`, keyed by
   * `question.name`. Built once on mount and never mutated — used as the
   * ground truth when we need to know what the user's original answer was
   * for a given question.
   */
  const originalAnswersByNameRef = useRef<Map<string, Answer>>(
    buildOriginalAnswerMap(originalDocumentRef.current),
  );

  /**
   * Set of question names whose `answer` is currently patched away from
   * the original. Backed by real state (not a ref) so updates trigger a
   * re-render and the `shouldShowUserFeedBack` flag stays in sync.
   */
  const [updatedAnswerQuestionNames, setUpdatedAnswerQuestionNames] = useState<
    Set<string>
  >(() => new Set());

  const [questions, setQuestions] = useState<QuestionForUI[]>(
    () =>
      transformCmmWorkflowToQuestionnaireForUI(
        wrapAsWorkflowDocument(currentDocument),
      ).questions,
  );

  const visibleQuestions = useMemo(
    () => getVisibleQuestionChain(questions),
    [questions],
  );

  const isQuestionnaireValid = useMemo(
    () =>
      validateQuestionnaireAnswers(
        visibleQuestions,
        originalAnswersByNameRef.current,
      ),
    [visibleQuestions],
  );

  // -------------------------------------------------------------------------
  // Internal mutators — each is a single, named workflow-document operation
  // that the user-facing handlers below compose. Keeping them tiny makes the
  // three public handlers read like a story instead of a state-machine.
  // -------------------------------------------------------------------------

  /** Returns whether the supplied `name`'s answer differs from its pristine answer. */
  const isAnswerChangedFromOriginal = (
    name: string,
    nextAnswer: Answer,
  ): boolean => {
    const originalAnswer = originalAnswersByNameRef.current.get(name);
    return !areAnswersEqual(originalAnswer, nextAnswer);
  };

  /** Marks (or unmarks) a question as "edited away from its pristine answer". */
  const markAnswerChanged = (name: string, isChanged: boolean) => {
    setUpdatedAnswerQuestionNames((prev) =>
      toggleNameInSet(prev, name, isChanged),
    );
  };

  /** Clears the named question from the "edited" set if it's there. */
  const clearAnswerChanged = (name: string) => {
    setUpdatedAnswerQuestionNames((prev) => toggleNameInSet(prev, name, false));
  };

  /**
   * Applies a workflow-helper patch to the current document, refreshes
   * the questions state from the helper's return, and stores the new
   * document. Used by both {@link handlePatch} and {@link handleCancel}.
   */
  const applyWorkflowPatch = (patchArg: WorkflowPatchArg) => {
    setWorkflowDocument((prevDoc) => {
      const { document: nextDoc, questions: nextQuestions } =
        applyPatchToCmmWorkflow(wrapAsWorkflowDocument(prevDoc), patchArg);
      setQuestions(nextQuestions);
      return unwrapWorkflowDocument(nextDoc);
    });
  };

  /** Drops any in-progress user feedback attached to `name`. */
  const clearUserFeedbackFor = (name: string) => {
    setWorkflowDocument((prevDoc) =>
      unwrapWorkflowDocument(
        clearUserFeedbackFromCmmWorkflow(wrapAsWorkflowDocument(prevDoc), name),
      ),
    );
  };

  /**
   * Appends `feedback` to the named question and re-derives the question
   * list (uses `transformCmmWorkflowToQuestionnaireForUI` so any UI-side
   * flags like `shouldShowTextArea` re-compute from the new doc).
   */
  const appendUserFeedbackFor = (name: string, feedback: UserFeedback) => {
    setWorkflowDocument((prevDoc) => {
      const nextDoc = appendUserFeedbackToCmmWorkflow(
        wrapAsWorkflowDocument(prevDoc),
        name,
        feedback,
      );
      setQuestions(transformCmmWorkflowToQuestionnaireForUI(nextDoc).questions);
      return unwrapWorkflowDocument(nextDoc);
    });
  };

  /** Builds a {@link UserFeedback} record from the in-question submit payload. */
  const buildUserFeedback = (payload: FeedbackPayload): UserFeedback => ({
    selected_option: payload.selectedValues.join(","),
    comment: payload.comments,
    created_at: new Date().toISOString(),
    created_by: userEmail,
  });

  // -------------------------------------------------------------------------
  // Public handlers
  // -------------------------------------------------------------------------

  /**
   * Applies a patch (answer change or free-text change) to the workflow
   * document, recomputes the questions, and updates the
   * "answer-changed-since-pristine" tracking set.
   *
   * @param patch - Either an `{ name, answer }` or
   *                `{ name, free_text_answer: { name, answer } }` patch
   */
  const handlePatch = (patch: QuestionPatch) => {
    if ("answer" in patch) {
      const isChanged = isAnswerChangedFromOriginal(patch.name, patch.answer);
      markAnswerChanged(patch.name, isChanged);
      if (!isChanged) {
        clearUserFeedbackFor(patch.name);
      }
    }
    applyWorkflowPatch(toWorkflowPatchArg(patch));
  };

  /**
   * Appends the supplied {@link UserFeedback} to the question identified
   * by `payload.questionName` and clears that name from the
   * "answer-changed" set.
   *
   * @param payload - Feedback details plus the target `questionName`
   */
  const handleSubmit = (payload: FeedbackPayload) => {
    appendUserFeedbackFor(payload.questionName, buildUserFeedback(payload));
    clearAnswerChanged(payload.questionName);
  };

  /**
   * Reverts the named question to its pristine answer and clears it from
   * the "answer-changed" set.
   *
   * @param payload - `{ questionName }` of the question to revert
   */
  const handleCancel = (payload: { questionName: string }) => {
    const originalAnswer = originalAnswersByNameRef.current.get(
      payload.questionName,
    );
    applyWorkflowPatch({
      name: payload.questionName,
      answer: originalAnswer ?? "",
    });
    clearAnswerChanged(payload.questionName);
  };

  return {
    workflowDocument,
    visibleQuestions,
    isQuestionnaireValid,
    updatedAnswerQuestionNames,
    handlePatch,
    handleSubmit,
    handleCancel,
  };
};
